import React, { useState } from 'react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { tenantAPI } from '@/lib/api';

/** Minutes since an ISO timestamp, or null. */
function ageMinutes(iso) {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.round((Date.now() - t) / 60000));
}

export function formatAge(iso) {
    const m = ageMinutes(iso);
    if (m === null) return 'never';
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 48) return `${h} h ago`;
    return `${Math.round(h / 24)} days ago`;
}

/**
 * Per-company inbox ingest: the opt-in switch, the interval, and the poll status.
 *
 * OFF unless explicitly switched on here. Absent means off server-side, so no client is polled
 * without someone making that decision on this card.
 *
 * The status block exists because the failure mode of polling is SILENCE — a revoked credential
 * and "no new documents this week" look identical unless something says which it was. That is how
 * outbound mail died unnoticed for weeks, so the last error is shown, not just the last success.
 */
export default function InboxIngestPanel({ tenant, onSaved }) {
    const settings = tenant?.settings || {};
    const status = settings.inbox_status || {};
    const [enabled, setEnabled] = useState(!!settings.inbox_ingest_enabled);
    const [minutes, setMinutes] = useState(String(settings.inbox_poll_minutes || 30));
    const [busy, setBusy] = useState(false);
    const [polling, setPolling] = useState(false);
    const [lastRun, setLastRun] = useState(null);

    const failures = status.consecutive_failures || 0;
    const hasError = !!status.last_error;

    /**
     * Fetch once, now. For a demo, and for the accountant whose client has just sent something
     * and who does not want to wait out the interval.
     *
     * The result is shown on the card rather than only in a toast: "how many, and why were the
     * rest skipped" is the whole point, and a toast is gone before it can be read. The server
     * reports skips already grouped, separating the normal case (someone has already handled the
     * document in the client's own system) from a genuine problem.
     */
    const pollNow = async () => {
        setPolling(true);
        setLastRun(null);
        try {
            const { data } = await tenantAPI.inboxPollNow(tenant.id);
            setLastRun(data);
            if (data.ok) {
                if (data.created > 0) toast.success(data.message);
                else toast.info(data.message);
            } else {
                toast.error(data.message || 'Hentning fejlede');
            }
            // Refresh the card so the status block and document counts reflect the new documents.
            if (onSaved) await onSaved();
        } catch (e) {
            const d = e?.response?.data?.detail;
            const msg = d?.message || d || 'Kunne ikke hente nu';
            setLastRun({ ok: false, error: msg, rate_limited: e?.response?.status === 429 });
            toast.error(msg);
        } finally {
            setPolling(false);
        }
    };

    const save = async (nextEnabled, nextMinutes) => {
        setBusy(true);
        try {
            await tenantAPI.updateAccountingProfile(tenant.id, {
                inbox_ingest_enabled: nextEnabled,
                inbox_poll_minutes: parseInt(nextMinutes, 10) || 30,
            });
            toast.success(nextEnabled ? 'Inbox-hentning slået til' : 'Inbox-hentning slået fra');
            if (onSaved) await onSaved();
        } catch (e) {
            // A protected client refuses to arm — surface the server's reason verbatim rather
            // than a generic failure, because "this client declined" is not a bug.
            const d = e?.response?.data?.detail;
            toast.error(d?.message || d || 'Kunne ikke gemme');
            setEnabled(!!settings.inbox_ingest_enabled);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rounded-md border p-3 space-y-3" data-testid="inbox-ingest-panel">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-medium">Hent bilag fra kundens egen indbakke</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Henter bilag som endnu ingen har rørt, og lægger dem i din kø. Gennemgang og
                        levering er uændret.
                    </p>
                </div>
                <Switch
                    checked={enabled}
                    disabled={busy}
                    data-testid="inbox-ingest-toggle"
                    onCheckedChange={(v) => { setEnabled(v); save(v, minutes); }}
                />
            </div>

            {enabled && (
                <div className="flex items-center gap-2">
                    <label className="text-xs text-muted-foreground">Hent hvert</label>
                    <Input
                        type="number" min={5} className="h-8 w-20" value={minutes} disabled={busy}
                        data-testid="inbox-poll-minutes"
                        onChange={(e) => setMinutes(e.target.value)}
                    />
                    <span className="text-xs text-muted-foreground">minutter</span>
                    <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => save(true, minutes)}>Gem</Button>
                </div>
            )}

            {enabled && (
                <div className="text-xs space-y-1 border-t pt-2" data-testid="inbox-ingest-status">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">
                            Sidst hentet <span>{formatAge(status.last_poll_at)}</span>
                        </span>
                        <Button
                            size="sm" variant="outline" className="h-7"
                            disabled={polling || busy}
                            data-testid="inbox-poll-now"
                            onClick={pollNow}
                        >
                            {polling ? 'Henter…' : 'Hent nu'}
                        </Button>
                    </div>

                    {lastRun && (
                        <div className="rounded border bg-muted/40 p-2 mt-1" data-testid="inbox-poll-result">
                            {lastRun.ok ? (
                                <>
                                    <p className="font-medium" data-testid="inbox-poll-created">
                                        {lastRun.created > 0
                                            ? `Hentede ${lastRun.created} nye bilag`
                                            : 'Ingen nye bilag'}
                                        <span className="font-normal text-muted-foreground">
                                            {` — set ${lastRun.seen} i alt`}
                                        </span>
                                    </p>
                                    {(lastRun.skipped || []).length > 0 && (
                                        <ul className="mt-1 space-y-0.5" data-testid="inbox-poll-skipped">
                                            {lastRun.skipped.map((s, i) => (
                                                <li key={i} className={s.kind === 'problem'
                                                    ? 'text-amber-700' : 'text-muted-foreground'}>
                                                    {`${s.count} sprunget over — ${s.reason}`}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            ) : (
                                <p className="text-red-800" data-testid="inbox-poll-failed">
                                    {lastRun.rate_limited
                                        ? 'Der blev hentet for et øjeblik siden — prøv igen om lidt.'
                                        : `Hentning fejlede: ${lastRun.error}`}
                                </p>
                            )}
                        </div>
                    )}
                    {status.last_created != null && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Nye bilag sidst</span>
                            <span>{status.last_created}</span>
                        </div>
                    )}
                    {hasError ? (
                        <div className="rounded bg-red-50 border border-red-200 text-red-800 p-2 mt-1"
                            data-testid="inbox-ingest-error">
                            <p className="font-medium">
                                Hentning fejler{failures > 1 ? ` (${failures} gange i træk)` : ''}
                            </p>
                            <p className="mt-0.5 break-words">{status.last_error}</p>
                            <p className="mt-1 text-[11px]">
                                Ingen nye bilag hentes før dette er løst — tjek forbindelsen ovenfor.
                            </p>
                        </div>
                    ) : (
                        status.last_ok_at && (
                            <div className="flex justify-between gap-2">
                                <span className="text-muted-foreground">Status</span>
                                <span className="text-emerald-700">OK</span>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}
